import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { SessionType, logRocketIdentifyAdvisorUser, logRocketIdentifyClientUser } from '@shared';
import { FullScreenLoadingOverlay } from './FullScreenLoadingOverlay';
import type { ChildrenProps } from '../types/ChildrenProps';
import {
  closeSession,
  fetchSessionForAuthRequest,
  selectSession,
  storeAuth0Session,
} from '../model';
import { Login } from '../pages/Login';
import { DuplicateAccountScreen } from '@client/pages/DuplicateAccountScreen';
import { getPublicRoutePaths } from '../Router';
import { setAuthToken, clearAuthToken } from '@client/api/queryClient';
import { useSignOut } from './hooks/use-sign-out';

export function SessionLoader({ children }: ChildrenProps) {
  const session = useSelector(selectSession);
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0();
  const signOut = useSignOut();

  // Check if current path is a public route
  const publicRoutes = getPublicRoutePaths();
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));

  const isAuthenticatedAndUserLoaded = isAuthenticated && user && user.sub;
  const isLoadingOrSessionLoading = isLoading || session.isLoading;

  useEffect(() => {
    // load auth0 session
    async function loadAuth0Session() {
      if (!isAuthenticatedAndUserLoaded) {
        clearAuthToken();
        dispatch(closeSession());
        return;
      }
      try {
        const authUserId = user.sub!;
        const accessToken = await getAccessTokenSilently();
        setAuthToken(accessToken);
        dispatch(
          storeAuth0Session({
            authUserId,
            accessToken,
          }),
        );
        dispatch(fetchSessionForAuthRequest());
      } catch (error) {
        clearAuthToken();
        dispatch(closeSession());
      }
    }
    if (session.sessionType === SessionType.unknown && !isLoadingOrSessionLoading) {
      loadAuth0Session();
    }
  }, [dispatch, session.sessionType, isAuthenticated, isLoading, user, session.isLoading]);

  // identify user in logrocket
  useEffect(() => {
    if (isLoading) {
      return;
    }

    // Handle client users
    if (session.sessionType === SessionType.client && isAuthenticated) {
      const { auth0Session } = session;
      const auth0UserId = auth0Session?.authUserId;
      const clientEmail = auth0Session?.clientUser?.email;
      if (auth0UserId && clientEmail) {
        logRocketIdentifyClientUser(auth0UserId, clientEmail);
      }
      return;
    }

    // Handle advisor users
    if (session?.advisor?.advisorId) {
      const advisorId = session.advisor.advisorId;
      logRocketIdentifyAdvisorUser(advisorId, session.advisor.advisorFirstName);
      return;
    }
  }, [session.sessionType, session.advisor, session.auth0Session, isAuthenticated, isLoading]);

  // Logout from Auth0 auth session is expired.
  useEffect(() => {
    // Access tokens can expire while the Auth0 session remains active/authenticated.
    if (session.sessionType === SessionType.unauthenticated && isAuthenticated && !isLoading) {
      signOut();
    }
  }, [session.sessionType, isAuthenticated, isLoading, signOut]);

  if (session.sessionType === SessionType.unknown || isLoadingOrSessionLoading) {
    return <FullScreenLoadingOverlay isLoading={true} />;
  }

  // Allow public routes to render without authentication
  if (isPublicRoute) {
    return children;
  }

  // display login page if user is not authenticated or user is not loaded
  if (!isAuthenticatedAndUserLoaded || session.sessionType === SessionType.unauthenticated) {
    return <Login />;
  }

  if (session.sessionType === SessionType.duplicateAccount) {
    return <DuplicateAccountScreen />;
  }

  return session.sessionType !== SessionType.unknown ? children : null;
}
